import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FilterKey, keyToEmptyFilter, SearchContext, SortKey} from 'search/SearchConstants';
import ItemSearchInput, {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import {updateWorklists} from 'worklists/WorklistActions';

import {WorklistSummariesById, WorklistSummary, WorklistType} from './WorklistConstants';

export interface WorklistSearchResponse {
  ids: Array<string>;
  total: number;
  worklistSummaries: Array<WorklistSummary>;
}

function getOptionDatasFromResponse(response: WorklistSearchResponse): Array<OptionData> {
  const summaries = response.worklistSummaries;
  const summaryMap: WorklistSummariesById = {};
  for (const summary of summaries) {
    summaryMap[summary.id] = summary;
  }
  // Put fetched worklists in the store so the chips don't have to fetch again
  updateWorklists(summaryMap);
  return summaries.map((ws) => ({
    name: ws.name,
    icon: ModelTypeConstants.WORKLIST,
    value: {
      modifiedAt: ws.modifiedAt,
      item: ws,
      item_id: ws.id,
      name: ws.name,
      type: APIUtil.apiIdToType(ws.id),
    },
  }));
}

export interface WorklistSearchInputProps {
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (worklist: WorklistSummary | null) => void;
  worklistTypes: Array<WorklistType>;
  context?: SearchContext;
}

/**
 * Component that searches for worklists by hitting SightGlass search. It only allows selecting a single
 * worklist.
 */
export default function WorklistSearchInput(props: WorklistSearchInputProps): JSX.Element {
  const [selectedWorklist, setSelectedWorklist] = useState<WorklistSummary | null>(null);

  return (
    <WorklistMultiSearchInput<WorklistSummary>
      disabled={Boolean(props.disabled)}
      autoFocus={props.autoFocus}
      selectedItems={selectedWorklist ? [selectedWorklist] : []}
      worklistTypes={props.worklistTypes}
      onChange={(selectedWorklists: Array<WorklistSummary>) => {
        if (selectedWorklists.length) {
          setSelectedWorklist(selectedWorklists[0]);
          props.onChange(selectedWorklists[0]);
        } else {
          setSelectedWorklist(null);
          props.onChange(null);
        }
      }}
      shouldAllowMultiple={false}
    />
  );
}

export interface WorklistMultiSearchInputProps<TItem> {
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (worklist: Array<TItem>) => void;
  worklistTypes: Array<WorklistType>;
  selectedItems: Array<TItem>;
  shouldAllowMultiple: boolean;
  context?: SearchContext;
  placeholder?: string;
}

/**
 * Component that searches for worklists.
 */
export function WorklistMultiSearchInput<TItem extends Item>(
  props: WorklistMultiSearchInputProps<TItem>
): JSX.Element {
  return (
    <ItemSearchInput<TItem>
      disabled={Boolean(props.disabled)}
      autoFocus={props.autoFocus}
      itemKey="worklists"
      searchURL="/1/api/worklists:search"
      selectedItems={props.selectedItems}
      onChange={props.onChange}
      fetchParams={{
        limit: 10,
        filters: [{...keyToEmptyFilter(FilterKey.WORKLIST_TYPE), value: props.worklistTypes}],
        offset: 0,
        sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
        context: props.context,
      }}
      getOptionDatasFromResponse={getOptionDatasFromResponse}
      // Prepopulate the dropdown so the user doesn't have to search most of the time
      searchOnEmptyQuery={true}
      shouldAllowMultiple={props.shouldAllowMultiple}
      placeholder={props.placeholder}
    />
  );
}
